import { AIProvider } from '@pique/core/blocksuite/presets/ai';
import { recordingStartedSlot } from '@pique/core/components/blocksuite/block-suite-editor/specs/custom/pique/pique-slots';

import {
  CopilotClient,
  type TranscriptionChunk,
  type TranscriptionProvider,
  type TranscriptionStreamController,
} from '../components/blocksuite/block-suite-editor/ai/copilot-client';
import { TranscriptionSource } from '../hooks/transcription-source-config';
import type { IAudioHandler } from './audio-handler-interface';
import type { TranscriptionService } from './transcription-service';
import { TranscriptionStatus } from './transcription-service';

interface TranscriptionState {
  isReady: boolean;
  isRecording: boolean;
}

const RECORDER_WORKLET_CODE = `
class RecorderProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.amplitudeHistory = [];
    this.maxHistoryLength = 10;
  }

  process(inputs) {
    const inputChannelData = inputs[0];
    if (!inputChannelData || !inputChannelData[0]) {
      return true;
    }

    const samples = inputChannelData[0];
    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
      sum += samples[i] * samples[i];
    }
    const rms = Math.sqrt(sum / samples.length);

    this.amplitudeHistory.push(rms);
    if (this.amplitudeHistory.length > this.maxHistoryLength) {
      this.amplitudeHistory.shift();
    }

    const avgAmplitude = this.amplitudeHistory.reduce((a, b) => a + b, 0) / this.amplitudeHistory.length;
    const cloned = new Float32Array(samples);

    this.port.postMessage({
      audioData: cloned,
      amplitude: avgAmplitude
    });

    return true;
  }
}
registerProcessor('recorder-processor', RecorderProcessor);
`;

export class BrowserAudioHandler implements IAudioHandler {
  private audioCtx: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private stream: MediaStream | null = null;

  private transcriptionController: TranscriptionStreamController | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private copilotClient: CopilotClient;
  private isStreamActive: boolean = false;
  private transcriptionState: TranscriptionState = {
    isReady: false,
    isRecording: false,
  };

  constructor(
    private readonly transcriptionService: TranscriptionService
  ) {
    this.copilotClient = new CopilotClient();
  }

  private async createMediaStreamFromDevice(deviceId: string): Promise<MediaStream> {
    if (deviceId.startsWith('virtual-system-audio-')) {
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true,
          systemAudio: 'include',
        } as MediaStreamConstraints & { systemAudio?: 'include' | 'exclude' });
        const audioTrack = stream.getAudioTracks()[0];
        if (audioTrack) {
          return new MediaStream([audioTrack]);
        }
        stream.getTracks().forEach(t => t.stop());
      } catch (err) {
        console.warn('[BrowserAudioHandler] getDisplayMedia failed:', err);
      }
      return navigator.mediaDevices.getUserMedia({ audio: true });
    }

    return navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: deviceId } },
    });
  }

  private getVendorName(): 'Deepgram' | 'AwsTranscribe' | 'WebSocket' {
    const source = this.transcriptionService.transcriptionSource$.value;
    if (source === TranscriptionSource.WEB_GEMINI_FLASH) {
      return 'WebSocket';
    }
    return 'Deepgram';
  }

  private getProvider(): TranscriptionProvider | undefined {
    const source = this.transcriptionService.transcriptionSource$.value;
    if (source === TranscriptionSource.WEB_GEMINI_FLASH) {
      return 'google-speech';
    }
    return undefined;
  }

  private handleTranscriptionResult(chunk: TranscriptionChunk): void {
    const isFinal = Boolean(chunk.isFinal);
    const transcript = chunk.text || '';

    if (!transcript) return;
    if (!this.transcriptionState.isReady || !this.transcriptionState.isRecording) return;

    const elapsedSec = Math.floor(chunk.timestamp);

    this.transcriptionService.setLatestTranscriptionChunk({
      text: transcript,
      timestamp: elapsedSec,
      isFinal,
      vendor: this.getVendorName(),
      audioSource: 'mic',
    });

    AIProvider.slots.toggleChatCards.emit({ visible: true, ok: true });
  }

  private async initializeTranscription(): Promise<void> {
    if (!this.stream) return;

    try {
      this.transcriptionController = this.copilotClient.createTranscriptionStream();

      this.transcriptionController.onResult((chunk: TranscriptionChunk) => {
        this.handleTranscriptionResult(chunk);
      });

      this.transcriptionController.onError((error: Error) => {
        console.error('[BrowserAudioHandler] Transcription error:', error);
        this.transcriptionState.isReady = false;
        this.transcriptionState.isRecording = false;
      });

      this.transcriptionController.onClose(() => {
        if (
          this.transcriptionService.transcriptionStatus$.value ===
          TranscriptionStatus.RECORDING
        ) {
          this.transcriptionService.setTranscriptionStatus(
            TranscriptionStatus.PAUSED
          );
        }
        this.transcriptionState.isRecording = false;
      });

      const provider = this.getProvider();
      const isGoogleSpeech = provider === 'google-speech';

      console.log(
        `[BrowserAudioHandler] Starting transcription with provider=${provider ?? 'deepgram (default)'}`
      );

      const started = await this.transcriptionController.start({
        model: isGoogleSpeech ? undefined : 'nova-2-meeting',
        smartFormat: !isGoogleSpeech,
        interimResults: true,
        punctuate: true,
        provider,
      });

      if (!started) {
        throw new Error('Failed to start transcription on backend');
      }

      this.transcriptionState.isReady = true;

      const possibleTypes = [
        'audio/webm; codecs=opus',
        'audio/webm',
        'audio/ogg; codecs=opus',
        '',
      ];

      let chosenType = '';
      for (const type of possibleTypes) {
        if (!type || MediaRecorder.isTypeSupported(type)) {
          chosenType = type;
          break;
        }
      }

      const options: MediaRecorderOptions = chosenType
        ? { mimeType: chosenType }
        : {};

      try {
        this.mediaRecorder = new MediaRecorder(this.stream, options);
        this.mediaRecorder.start(300);
      } catch (mediaRecorderError) {
        console.error('[BrowserAudioHandler] MediaRecorder creation/start failed:', mediaRecorderError);
        this.transcriptionState.isReady = false;
        this.transcriptionState.isRecording = false;
        if (this.transcriptionController) {
          this.transcriptionController.stop();
          this.transcriptionController.disconnect();
          this.transcriptionController = null;
        }
        throw mediaRecorderError;
      }

      this.mediaRecorder.onstart = () => {
        this.transcriptionState.isRecording = true;
      };

      this.mediaRecorder.ondataavailable = e => {
        if (
          this.transcriptionController?.isConnected() &&
          this.transcriptionState.isRecording &&
          this.mediaRecorder?.state !== 'inactive'
        ) {
          this.transcriptionController.sendAudio(e.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        this.transcriptionState.isRecording = false;
      };

      this.mediaRecorder.onerror = err => {
        console.error('[BrowserAudioHandler] MediaRecorder error:', err);
        this.transcriptionState.isRecording = false;
      };
    } catch (error) {
      console.error('[BrowserAudioHandler] Failed to initialize transcription:', error);
      this.transcriptionState.isReady = false;
      this.transcriptionState.isRecording = false;
      this.transcriptionController = null;
    }
  }

  public async start(): Promise<void> {
    if (this.isStreamActive && this.stream) {
      if (!this.transcriptionController) {
        await this.initializeTranscription();
      }
      return;
    }

    try {
      let stream: MediaStream;
      const selectedDeviceId = this.transcriptionService.getSelectedDeviceId();

      if (selectedDeviceId) {
        stream = await this.createMediaStreamFromDevice(selectedDeviceId);
      } else {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: 16000,
          },
        });
      }

      this.stream = stream;
      this.isStreamActive = true;

      await this.initializeTranscription();

      const audioCtx = new AudioContext({ sampleRate: 16000 });
      this.audioCtx = audioCtx;

      const recorderNode = await this.createAudioWorkletNode(audioCtx);
      this.workletNode = recorderNode;

      const sourceNode = audioCtx.createMediaStreamSource(stream);
      this.sourceNode = sourceNode;

      sourceNode.connect(recorderNode);

      recordingStartedSlot.emit();
    } catch (error) {
      console.error('[BrowserAudioHandler] Start browser recording error:', error);
      this.transcriptionService.setAmplitude({ mic: 0, speaker: 0, scaled: 0 });
    }
  }

  private async createAudioWorkletNode(audioCtx: AudioContext): Promise<AudioWorkletNode> {
    const codeBlob = new Blob([RECORDER_WORKLET_CODE], { type: 'application/javascript' });
    const blobUrl = URL.createObjectURL(codeBlob);
    await audioCtx.audioWorklet.addModule(blobUrl);

    const node = new AudioWorkletNode(audioCtx, 'recorder-processor');

    node.port.onmessage = event => {
      const data = event.data;

      if (data && typeof data === 'object' && data.audioData && typeof data.amplitude === 'number') {
        const scaledAmplitude = data.amplitude * 30;
        const amplitudeData = {
          mic: scaledAmplitude,
          speaker: 0,
          scaled: Math.min(1, scaledAmplitude / 5),
        };

        this.transcriptionService.setAmplitude(amplitudeData);
      }
    };

    return node;
  }

  public async stop(): Promise<void> {
    try {
      this.transcriptionState.isRecording = false;

      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
      this.mediaRecorder = null;

      if (this.transcriptionController) {
        this.transcriptionController.stop();
        this.transcriptionController.disconnect();
        this.transcriptionController = null;
      }

      this.transcriptionState.isReady = false;

      if (this.sourceNode) {
        this.sourceNode.disconnect();
        this.sourceNode = null;
      }

      if (this.workletNode) {
        if (this.workletNode.port.onmessage) {
          this.workletNode.port.onmessage = null;
        }
        this.workletNode.disconnect();
        this.workletNode = null;
      }

      if (this.audioCtx && this.audioCtx.state !== 'closed') {
        await this.audioCtx.close();
        this.audioCtx = null;
      }

      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
        this.isStreamActive = false;
      }

      this.transcriptionService.setAmplitude({ mic: 0, speaker: 0, scaled: 0 });
    } catch (error) {
      console.error('[BrowserAudioHandler] Stop browser recording error:', error);
      this.transcriptionService.setAmplitude({ mic: 0, speaker: 0, scaled: 0 });
      this.isStreamActive = false;
      this.transcriptionState.isReady = false;
      this.transcriptionState.isRecording = false;
    }
  }

  public getMediaStream(): MediaStream | null {
    return this.stream;
  }

  public dispose(): void {
    this.stop().catch(error => {
      console.error('[BrowserAudioHandler] Error during dispose:', error);
    });
    this.isStreamActive = false;
    this.transcriptionState.isReady = false;
    this.transcriptionState.isRecording = false;
    this.transcriptionService.setAmplitude({ mic: 0, speaker: 0, scaled: 0 });
  }
}
